import { useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Search, Workflow, User, Users, LayoutGrid, List, ArrowDownUp, Star } from 'lucide-react';
import { WORKFLOW_CATEGORIES } from '@/lib/workflowCategories';
import WorkflowCard from '@/components/WorkflowCard';
import { useFavorites } from '@/hooks/useFavorites';
import type { WorkflowSummary } from '@/hooks/useRealtimeWorkflows';

type Scope = 'mine' | 'shared' | 'all';
type ViewMode = 'grid' | 'list';

// Shared across home + manager so the choice persists everywhere.
const VIEW_MODE_KEY = 'workflow-view-mode';

const loadViewMode = (): ViewMode => {
  try {
    return localStorage.getItem(VIEW_MODE_KEY) === 'list' ? 'list' : 'grid';
  } catch {
    return 'grid';
  }
};

type SortKey = 'name-asc' | 'name-desc' | 'creator-asc' | 'creator-desc' | 'created-desc' | 'created-asc';

const SORT_OPTIONS: { id: SortKey; label: string }[] = [
  { id: 'created-desc', label: 'Newest first' },
  { id: 'created-asc', label: 'Oldest first' },
  { id: 'name-asc', label: 'Name (A–Z)' },
  { id: 'name-desc', label: 'Name (Z–A)' },
  { id: 'creator-asc', label: 'Creator (A–Z)' },
  { id: 'creator-desc', label: 'Creator (Z–A)' },
];

const sortWorkflows = (list: WorkflowSummary[], key: SortKey): WorkflowSummary[] => {
  const sorted = [...list];
  switch (key) {
    case 'name-asc':
      return sorted.sort((a, b) => a.name.localeCompare(b.name));
    case 'name-desc':
      return sorted.sort((a, b) => b.name.localeCompare(a.name));
    case 'creator-asc':
      return sorted.sort((a, b) => a.ownerName.localeCompare(b.ownerName) || a.name.localeCompare(b.name));
    case 'creator-desc':
      return sorted.sort((a, b) => b.ownerName.localeCompare(a.ownerName) || a.name.localeCompare(b.name));
    case 'created-asc':
      return sorted.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    case 'created-desc':
    default:
      return sorted.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }
};

interface WorkflowBrowserProps {
  workflows: WorkflowSummary[];
  // Current user's uid, used to split "mine" vs "shared with me".
  currentUserId?: string | null;
  // Build the link target for a workflow (manager detail vs run page).
  linkTo: (wf: WorkflowSummary) => string;
  // Manager view shows status/edit; run view shows Run.
  managerView?: boolean;
  // Whether to show the Mine/Shared/All scope filter (hidden on the public home).
  showScope?: boolean;
  defaultScope?: Scope;
  // When true, only one category can be active at a time (click again to clear).
  singleCategory?: boolean;
  // Archive control (optional — omit to hide the archive button).
  onToggleArchive?: (id: string) => void;
}

// Manager view sees every workflow, so "not mine" means another manager made it;
// end users only see what's shared with them.
const getScopes = (managerView: boolean): { id: Scope; label: string; icon: typeof User }[] => [
  { id: 'mine', label: 'My Workflows', icon: User },
  { id: 'shared', label: managerView ? 'Created by Others' : 'Shared with Me', icon: Users },
  { id: 'all', label: 'All', icon: LayoutGrid },
];

export default function WorkflowBrowser({
  workflows,
  currentUserId,
  linkTo,
  managerView = false,
  showScope = true,
  defaultScope = 'mine',
  singleCategory = false,
  onToggleArchive,
}: WorkflowBrowserProps) {
  const [scope, setScope] = useState<Scope>(defaultScope);
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('created-desc');
  const [onlyFavorites, setOnlyFavorites] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(loadViewMode);
  const { isFavorite, toggleFavorite } = useFavorites();

  const setView = (mode: ViewMode) => {
    setViewMode(mode);
    try {
      localStorage.setItem(VIEW_MODE_KEY, mode);
    } catch {
      /* ignore */
    }
  };

  const toggleCategory = (id: string) => {
    setSelectedCategories((prev) => {
      // Single-select: clicking the active one clears it, otherwise replace.
      if (singleCategory) return prev.has(id) ? new Set() : new Set([id]);
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Category counts respect the active scope but ignore the category filter
  // itself, so the sidebar always shows how many match each category.
  const scopedWorkflows = useMemo(() => {
    if (!showScope || scope === 'all' || !currentUserId) return workflows;
    if (scope === 'mine') return workflows.filter((w) => w.owner === currentUserId);
    return workflows.filter((w) => w.owner !== currentUserId);
  }, [workflows, scope, showScope, currentUserId]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const wf of scopedWorkflows) {
      for (const cat of wf.categories) counts[cat] = (counts[cat] || 0) + 1;
    }
    return counts;
  }, [scopedWorkflows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matched = scopedWorkflows.filter((wf) => {
      if (onlyFavorites && !isFavorite(wf.id)) return false;
      if (selectedCategories.size > 0 && !wf.categories.some((c) => selectedCategories.has(c))) return false;
      if (q && !wf.name.toLowerCase().includes(q) && !(wf.description || '').toLowerCase().includes(q)) return false;
      return true;
    });
    return sortWorkflows(matched, sortKey);
  }, [scopedWorkflows, selectedCategories, search, sortKey, onlyFavorites, isFavorite]);

  const scopeCounts = useMemo(() => {
    if (!currentUserId) return { mine: 0, shared: 0, all: workflows.length };
    const mine = workflows.filter((w) => w.owner === currentUserId).length;
    return { mine, shared: workflows.length - mine, all: workflows.length };
  }, [workflows, currentUserId]);

  // Favorites count reflects the scoped list so it matches what the toggle reveals.
  const favoriteCount = useMemo(() => scopedWorkflows.filter((w) => isFavorite(w.id)).length, [scopedWorkflows, isFavorite]);

  return (
    <div className="flex flex-col md:flex-row gap-6 min-h-0">
      {/* Left panel */}
      <aside className="w-full md:w-56 md:shrink-0 space-y-6">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            className="h-9 pl-8 text-sm"
          />
        </div>

        <div className="space-y-0.5">
          {showScope &&
            getScopes(managerView).map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setScope(id)}
                className={
                  'w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-sm transition-colors ' +
                  (scope === id ? 'bg-accent text-foreground font-medium' : 'text-muted-foreground hover:bg-accent/50')
                }
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="flex-1 text-left">{label}</span>
                <span className="text-[11px] text-muted-foreground/60">{scopeCounts[id]}</span>
              </button>
            ))}
          <button
            type="button"
            onClick={() => setOnlyFavorites((v) => !v)}
            className={
              'w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-sm transition-colors ' +
              (onlyFavorites
                ? 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300 font-medium'
                : 'text-muted-foreground hover:bg-accent/50')
            }
          >
            <Star className={'h-4 w-4 shrink-0 ' + (onlyFavorites ? 'fill-amber-400 text-amber-400' : '')} />
            <span className="flex-1 text-left">Favorites</span>
            <span className="text-[11px] text-muted-foreground/60">{favoriteCount}</span>
          </button>
        </div>

        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/60 px-2.5 mb-1.5">
            Workflow Type
          </p>
          <div className="space-y-0.5">
            {WORKFLOW_CATEGORIES.map(({ id, label, icon: Icon }) => {
              const count = categoryCounts[id] || 0;
              const active = selectedCategories.has(id);
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => toggleCategory(id)}
                  className={
                    'w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-sm transition-colors ' +
                    (active
                      ? 'bg-violet-50 text-violet-700 dark:bg-violet-500/10 dark:text-violet-300 font-medium'
                      : 'text-muted-foreground hover:bg-accent/50')
                  }
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="flex-1 text-left">{label}</span>
                  <span className="text-[11px] text-muted-foreground/60">{count}</span>
                </button>
              );
            })}
          </div>
          {!singleCategory && selectedCategories.size > 0 && (
            <button
              type="button"
              onClick={() => setSelectedCategories(new Set())}
              className="mt-2 px-2.5 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
            >
              Clear filters
            </button>
          )}
        </div>
      </aside>

      {/* List/grid */}
      <div className="flex-1 min-w-0">
        {/* Result count + view toggle + sort */}
        <div className="flex items-center justify-between mb-4 gap-3">
          <span className="text-xs text-muted-foreground shrink-0">
            {filtered.length} {filtered.length === 1 ? 'workflow' : 'workflows'}
          </span>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setView('grid')}
                className={
                  'p-1.5 rounded-md transition-colors ' +
                  (viewMode === 'grid' ? 'bg-accent text-foreground' : 'text-muted-foreground hover:text-foreground')
                }
                title="Card view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setView('list')}
                className={
                  'p-1.5 rounded-md transition-colors ' +
                  (viewMode === 'list' ? 'bg-accent text-foreground' : 'text-muted-foreground hover:text-foreground')
                }
                title="List view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
            <div className="flex items-center gap-1.5">
              <ArrowDownUp className="h-3.5 w-3.5 text-muted-foreground" />
              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value as SortKey)}
                className="text-xs h-8 rounded-md border border-border/60 bg-background px-2 cursor-pointer"
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-20">
            <Workflow className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No workflows match your filters</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((wf) => (
              <WorkflowCard
                key={wf.id}
                workflow={wf}
                to={linkTo(wf)}
                showStatus={managerView}
                view="grid"
                isFavorite={isFavorite(wf.id)}
                onToggleFavorite={toggleFavorite}
                onToggleArchive={onToggleArchive}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((wf) => (
              <WorkflowCard
                key={wf.id}
                workflow={wf}
                to={linkTo(wf)}
                showStatus={managerView}
                view="list"
                isFavorite={isFavorite(wf.id)}
                onToggleFavorite={toggleFavorite}
                onToggleArchive={onToggleArchive}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
